import { app } from '@microsoft/teams-js';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';

import { isRunningInTeams } from '@/lib/teams-env.js';
import { withTimeout } from '@/lib/with-timeout.js';
import { logger } from '@/logger.js';

import { onTeamsResume } from './teams-lifecycle.js';

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const QUALITY_RUN_RE =
  /^quality:([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

/** The first read waits as long as the sign-in path does; Teams can be slow to answer at boot. */
const TEAMS_CONTEXT_BOOT_TIMEOUT_MS = 8_000;

/** On resume the SDK is long initialised, so an answer that takes longer is not coming. */
const TEAMS_CONTEXT_RESUME_TIMEOUT_MS = 2_000;

/** Where a Teams deep link sends the user inside the app. */
export type TeamsDeepLinkTarget =
  | { to: '/quality/$id'; params: { id: string } }
  | { to: '/chats'; search: { conversationId: string } };

/**
 * Maps the `subPageId` Teams hands over (the `subEntityId` the server put on the notification's deep
 * link) to a route: `quality:{runId}` opens that quality run, a bare UUID opens that chat. Anything
 * else maps to nothing, so a tampered or legacy value can never send the user to an arbitrary route.
 */
export function teamsDeepLinkTarget(
  subPageId: string | null | undefined,
): TeamsDeepLinkTarget | null {
  if (!subPageId) return null;
  const qualityRunId = QUALITY_RUN_RE.exec(subPageId)?.[1];
  if (qualityRunId) return { to: '/quality/$id', params: { id: qualityRunId } };
  if (UUID_RE.test(subPageId)) {
    return { to: '/chats', search: { conversationId: subPageId } };
  }
  return null;
}

/**
 * The deep-link target Teams opened this tab with, or null when there is none. Makes no SDK call
 * outside Teams, where `app.initialize()` never settles, and treats a slow or failing SDK as "no deep
 * link" rather than an error.
 */
export async function readTeamsSubPageId(
  timeoutMs = TEAMS_CONTEXT_BOOT_TIMEOUT_MS,
): Promise<string | null> {
  if (!isRunningInTeams()) return null;
  try {
    await withTimeout(app.initialize(), timeoutMs, 'Teams app.initialize');
    const context = await withTimeout(
      app.getContext(),
      timeoutMs,
      'Teams app.getContext',
    );
    return context.page?.subPageId || null;
  } catch (error: unknown) {
    logger.debug('teams-deep-link: context unavailable', error);
    return null;
  }
}

/**
 * Routes the app to the right place when the user lands here from a Teams activity-feed deep link
 * (the bell that fires after an agent reply or when a quality run finishes), so the click "opens the
 * right thing" instead of dumping the user on the static-tab root.
 *
 * It reads the link once per mount. When Teams resumes a suspended tab, it reads the context again
 * (the resume callback's own entity data is unreliable, teams-js issue #1933) and follows a link only
 * if it differs from the one already handled; with no new link, a tab whose content URL is not the
 * app root is opened the way a fresh load would open it. Inert while `enabled` is false, which is how
 * the public share viewer keeps away from the Teams SDK.
 */
export function useTeamsDeepLink(enabled = true): void {
  const navigate = useNavigate();
  const handled = useRef(false);
  const lastSubPageId = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || handled.current) return;
    handled.current = true;
    let cancelled = false;
    void readTeamsSubPageId().then((subPageId) => {
      if (cancelled) return;
      lastSubPageId.current = subPageId;
      openDeepLink(navigate, subPageId);
    });
    return () => {
      cancelled = true;
    };
  }, [navigate, enabled]);

  useEffect(() => {
    if (!enabled) return;
    return onTeamsResume(async (context) => {
      const subPageId = await readTeamsSubPageId(
        TEAMS_CONTEXT_RESUME_TIMEOUT_MS,
      );
      if (subPageId !== null && subPageId !== lastSubPageId.current) {
        lastSubPageId.current = subPageId;
        if (openDeepLink(navigate, subPageId)) return;
      }
      openResumedContent(navigate, context.contentUrl);
    });
  }, [navigate, enabled]);
}

/** Navigates to the deep link's target; false when the link maps to nothing. */
function openDeepLink(
  navigate: ReturnType<typeof useNavigate>,
  subPageId: string | null,
): boolean {
  const target = teamsDeepLinkTarget(subPageId);
  if (target === null) return false;
  logger.debug('teams-deep-link: opening', target);
  void navigate(target);
  return true;
}

/** Opens the page a resumed tab asked for, unless it is the app root the user already moved on from. */
function openResumedContent(
  navigate: ReturnType<typeof useNavigate>,
  contentUrl: URL,
): void {
  if (contentUrl.pathname === '/' || contentUrl.pathname === '') return;
  void navigate({ href: `${contentUrl.pathname}${contentUrl.search}` });
}
